import { DEATH } from "@eldrun/content";
import type { CameraPose } from "./camera-rig.ts";

/** Aliado vivo que o espectador pode seguir. */
export interface SpectatorTarget {
  sessionId: string;
  x: number;
  y: number;
  z: number;
}

export interface SpectatorInput {
  /** Olhar do mouse (órbita). */
  yaw: number;
  pitch: number;
  /** Direção de movimento (câmera livre do Mutirão), -1…1 por eixo. */
  moveX: number;
  moveZ: number;
  /** +1 ao clicar (próximo aliado). */
  cycle: number;
  /** Câmera livre permitida (Mutirão). */
  free: boolean;
}

/** Velocidade da câmera livre (m/s). */
const FREE_SPEED = 10;
/** Altura do pivô sobre o aliado (m). */
const PIVOT = 1.5;

/**
 * Câmera de quem morreu (doc 02 §18): por 3 s sobe 2,5 m olhando o corpo; depois segue um
 * aliado vivo em órbita de 5 m (o mouse gira, o clique troca), encurtando o braço quando o
 * terreno fica no meio. No Mutirão, WASD solta a câmera até 40 m do aliado seguido.
 */
export class Spectator {
  private diedAt = -1;
  private index = 0;
  private offsetX = 0;
  private offsetZ = 0;
  private body = { x: 0, y: 0, z: 0, yaw: 0 };
  /** Quem está sendo seguido agora. */
  following: string | undefined;

  frame(
    now: number,
    dt: number,
    dead: boolean,
    body: { x: number; y: number; z: number; yaw: number },
    allies: readonly SpectatorTarget[],
    input: SpectatorInput,
    height: (x: number, z: number) => number,
  ): CameraPose | undefined {
    if (!dead) {
      this.diedAt = -1;
      this.following = undefined;
      return undefined;
    }
    if (this.diedAt < 0) {
      this.diedAt = now;
      this.body = { ...body };
      this.offsetX = 0;
      this.offsetZ = 0;
    }
    const t = (now - this.diedAt) / 1000;
    if (t < DEATH.deathCamera || allies.length === 0) {
      this.following = undefined;
      const rise = DEATH.deathCameraRise * Math.min(1, t / 1.5);
      return { x: this.body.x, y: this.body.y + 1.2 + rise, z: this.body.z + 0.01, yaw: this.body.yaw, pitch: -1.25 };
    }
    if (input.cycle !== 0) {
      this.index += input.cycle;
      this.offsetX = 0;
      this.offsetZ = 0;
    }
    const n = allies.length;
    const ally = allies[((this.index % n) + n) % n] as SpectatorTarget;
    this.following = ally.sessionId;
    if (input.free && (input.moveX !== 0 || input.moveZ !== 0)) {
      // yaw 0 olha para −z; frente = (−sin, −cos), direita = (cos, −sin).
      const sin = Math.sin(input.yaw);
      const cos = Math.cos(input.yaw);
      this.offsetX += (-sin * input.moveZ + cos * input.moveX) * FREE_SPEED * dt;
      this.offsetZ += (-cos * input.moveZ - sin * input.moveX) * FREE_SPEED * dt;
      const len = Math.hypot(this.offsetX, this.offsetZ);
      if (len > DEATH.freeCameraRadius) {
        this.offsetX *= DEATH.freeCameraRadius / len;
        this.offsetZ *= DEATH.freeCameraRadius / len;
      }
    }
    const px = ally.x + this.offsetX;
    const pz = ally.z + this.offsetZ;
    const py = Math.max(ally.y, height(px, pz)) + PIVOT;
    const cp = Math.cos(input.pitch);
    const backX = Math.sin(input.yaw) * cp;
    const backY = -Math.sin(input.pitch);
    const backZ = Math.cos(input.yaw) * cp;
    let distance: number = DEATH.spectatorDistance;
    for (let i = 1; i <= 8; i++) {
      const d = (DEATH.spectatorDistance * i) / 8;
      if (py + backY * d < height(px + backX * d, pz + backZ * d) + 0.35) {
        distance = Math.max(0.5, (DEATH.spectatorDistance * (i - 1)) / 8);
        break;
      }
    }
    return { x: px + backX * distance, y: py + backY * distance, z: pz + backZ * distance, yaw: input.yaw, pitch: input.pitch };
  }
}
